import { useState } from 'react'
import { Plus, ShieldOff } from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useGrantMutations, useGrants, useRolePicker, useUserPicker } from '../api'
import type { GrantView } from '../types'
import { Select } from '@/components/ui/select'

type Draft = { userId: string; roleId: string; legalEntityId: string; reason: string }

const EMPTY: Draft = { userId: '', roleId: '', legalEntityId: '', reason: '' }

/**
 * Who holds which role, in which entity.
 *
 * <p>A grant is per entity rather than global: permissions are evaluated against the entity
 * of the record being touched, so somebody can be a payroll administrator in one entity and
 * see nothing in another. A grant with no entity is the opt-in group-level one.
 *
 * <p>Revoked grants stay in the list, greyed. The row is the audit trail of who could reach
 * what and when — deleting it would answer "who has access now" and destroy "who had access
 * in March", which is the question an auditor actually asks.
 */
export function EntityGrantsPage() {
  const [granted, setGranted] = useState(false)
  useBreadcrumb()
  const [userFilter, setUserFilter] = useState('')
  const grants = useGrants(userFilter || undefined)
  const { create, revoke } = useGrantMutations()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')

  const [draft, setDraft] = useState<Draft | null>(null)
  const [toRevoke, setToRevoke] = useState<GrantView | null>(null)
  // Roles only while the dialog is open; users always, because the filter needs them too.
  const roles = useRolePicker(draft !== null)
  const users = useUserPicker()
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    if (!draft) return
    setError(null)
    try {
      await create.mutateAsync({
        userId: draft.userId,
        roleId: draft.roleId,
        legalEntityId: draft.legalEntityId || null,
        reason: draft.reason || null,
      })
      setDraft(null)
      setGranted(true)
    } catch (caught) {
      // Segregation-of-duties conflicts come back from the server with the pair named.
      setError(toApiProblem(caught)?.detail ?? 'That grant could not be made')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="User roles & access"
        subtitle="Roles are held per legal entity, not globally."
        actions={
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="grant-filter">Filter by user</Label>
              {users.isError ? (
                <Input
                  id="grant-filter" value={userFilter} placeholder="User id"
                  onChange={(event) => setUserFilter(event.target.value)} className="w-72"
                />
              ) : (
                <Select id="grant-filter" value={userFilter} className="w-72"
                        onChange={(event) => setUserFilter(event.target.value)}>
                  <option value="">All users</option>
                  {users.data?.map((user) => (
                    <option key={user.id} value={user.id}>{user.fullName} — {user.email}</option>
                  ))}
                </Select>
              )}
            </div>
            <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
              <Plus className="size-4" aria-hidden="true" />
              Grant a role
            </Button>
          </div>
        }
      />

      {granted && (
        <p role="status" className="text-sm text-[var(--color-success)]">
          Granted. They will see the change the next time they sign in, or within a few minutes if
          they are signed in now.
        </p>
      )}

      <Card>
        {grants.isPending && <Skeleton className="m-4 h-32" />}
        {grants.isError && <ErrorState error={grants.error} onRetry={() => void grants.refetch()} />}
        {grants.isSuccess && grants.data.length === 0 && (
          <EmptyState
            title="No grants"
            description="Nobody holds a role in any entity yet, or the filter matches nothing."
          />
        )}
        {(grants.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[48rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">User</th>
                  <th scope="col" className="p-3 font-medium">Role</th>
                  <th scope="col" className="p-3 font-medium">Entity</th>
                  <th scope="col" className="p-3 font-medium">Granted</th>
                  <th scope="col" className="p-3 font-medium">Reason</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {grants.data?.map((grant) => (
                  <tr
                    key={grant.id}
                    className={
                      grant.live
                        ? 'border-b border-[var(--color-border)] last:border-0'
                        : 'border-b border-[var(--color-border)] text-[var(--color-muted)] last:border-0'
                    }
                  >
                    <td className="p-3">{grant.userEmail}</td>
                    <td className="p-3">{grant.roleName}</td>
                    <td className="p-3">
                      {grant.legalEntityName ?? (
                        <span title="Applies across every entity">Group-wide</span>
                      )}
                    </td>
                    <td className="p-3"><DateValue value={grant.grantedAt} /></td>
                    <td className="p-3 max-w-64 truncate" title={grant.reason ?? undefined}>
                      {grant.reason ?? '—'}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={grant.live ? 'ACTIVE' : 'REVOKED'} />
                    </td>
                    <td className="p-3 text-right">
                      {grant.live && (
                        <Button
                          type="button" size="icon" variant="ghost"
                          aria-label={`Revoke ${grant.roleName} for ${grant.userEmail}`}
                          onClick={() => setToRevoke(grant)}
                        >
                          <ShieldOff className="size-4" aria-hidden="true" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft !== null}
        onOpenChange={(open) => !open && setDraft(null)}
        title="Grant a role in an entity"
        description="Leave the entity blank for a group-wide grant — which should be the exception."
        loading={create.isPending}
        error={error}
        onSubmit={() => void submit()}
      >
        {draft && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="grant-user" required>User</Label>
              {/* Pickers, not pasted ids: a typed name reached the API as an unparseable UUID.
                  The user list needs user-management rights; without them it falls back to an
                  id field rather than an empty dropdown. */}
              {users.isError ? (
                <Input id="grant-user" required value={draft.userId} placeholder="User id"
                       onChange={(e) => setDraft({ ...draft, userId: e.target.value })} />
              ) : (
                <Select id="grant-user" required value={draft.userId} className="w-full"
                        onChange={(e) => setDraft({ ...draft, userId: e.target.value })}>
                  <option value="" disabled>{users.isLoading ? 'Loading users…' : 'Select a user'}</option>
                  {users.data?.map((user) => (
                    <option key={user.id} value={user.id}>{user.fullName} — {user.email}</option>
                  ))}
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="grant-role" required>Role</Label>
              <Select id="grant-role" required value={draft.roleId} className="w-full"
                      onChange={(e) => setDraft({ ...draft, roleId: e.target.value })}>
                <option value="" disabled>{roles.isLoading ? 'Loading roles…' : 'Select a role'}</option>
                {roles.data?.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="grant-entity">Legal entity</Label>
              {/* A picker, not a pasted UUID. /v1/org/legal-entities did not exist until
                  part 5b, which is why this was a text box; row-level security narrows the
                  list to the entities the caller may actually grant in. */}
              <Select
                id="grant-entity"
                value={draft.legalEntityId}
                onChange={(e) => setDraft({ ...draft, legalEntityId: e.target.value })}
                className="w-full"
              >
                <option value="">Group-wide — every entity</option>
                {(entities.data ?? []).map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="grant-reason">Reason</Label>
              <Input id="grant-reason" value={draft.reason}
                     placeholder="Covering payroll for Q3"
                     onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
              <p className="text-xs text-[var(--color-muted)]">
                Kept with the grant. It is what makes the audit trail readable a year later.
              </p>
            </div>
          </>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toRevoke !== null}
        onOpenChange={(open) => !open && setToRevoke(null)}
        title="Revoke this grant?"
        description={
          toRevoke
            ? `${toRevoke.userEmail} loses ${toRevoke.roleName} in ${toRevoke.legalEntityName ?? 'every entity'}. The row stays for the audit trail.`
            : undefined
        }
        confirmLabel="Revoke"
        destructive
        loading={revoke.isPending}
        onConfirm={() => {
          if (toRevoke) revoke.mutate(toRevoke.id, { onSettled: () => setToRevoke(null) })
        }}
      />
    </div>
  )
}
